import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
} from "react";
import { AppPage, PharmacyListItem } from "../../components";
import { v1 as uuidv1 } from "uuid";
import { Link } from "react-router-dom";
import { hasLocalSession, useTranslate } from "../../helpers";
import { AppStore } from "../../app/store";
import { hasMailOrderPharmacy as getHasMailOrderPharmacy } from "../../app/store/AppStore";
import {
  ActionType,
  LocationWithNextRouteInState,
  PharmacyType,
} from "../../@types";
import routes from "../../app/routes";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import classNames from "classnames";
import { BeneNewPlanSearchLink } from "../../components/BeneNewPlanSearchLink";
import {
  clearMbpReferrerUri,
  clearMbpReferrerUriAndRedirect,
} from "../../helpers/mbpReferrerHelpers";
import messages from "../../translations/en-US.json";
import { PharmacyListItemProps } from "../../components/PharmacyListItem";
import {
  AnalyticsActionType,
  Ga4Event,
} from "../../app/contexts/Analytics/types";

interface ManagePharmaciesPageProps {
  isFromSummaryPage?: boolean;
}

const ManagePharmaciesPage: FunctionComponent<ManagePharmaciesPageProps> = ({
  isFromSummaryPage = false,
}) => {
  const {
    dispatch,
    state: { mbpReferrerUri, pharmacies, pharmacyType },
    state,
  } = useContext(AppStore.AppContext);
  const [toastId] = useState(uuidv1());
  const location = useLocation();
  const [hasMailOrderPharmacy, setHasMailOrderPharmacy] = useState(
    getHasMailOrderPharmacy(state)
  );
  const [nextRoute, setNextRoute] = useState<typeof location.state>();
  const isMySavedPharmacies = mbpReferrerUri !== "" || isFromSummaryPage;
  const t = useTranslate();
  const { search } = location;
  const noPharmaciesAdded = pharmacies.length === 0 && !hasMailOrderPharmacy;
  const managePharmaciesButtonTranslationKey =
    isMySavedPharmacies || nextRoute
      ? "ok"
      : "manage_pharmacies.actions.see_plans";

  // Analytics values (Use en-US translations only)
  const tealiumEventAction = isMySavedPharmacies
    ? "Saved Pharmacies"
    : "Confirm Pharmacies";
  const addPharmacyEventText = noPharmaciesAdded
    ? messages["manage_pharmacies.actions.add_a_pharmacy"]
    : messages["manage_pharmacies.actions.add_another_pharmacy"];
  const removePharmacyEventText =
    messages["manage_pharmacies.pharmacy.remove_pharmacy"];
  const removePharmacyEventLabel = isMySavedPharmacies
    ? "pharmacy removed"
    : removePharmacyEventText;

  // Extra virtual page view tracking as an event
  useEffect(() => {
    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: isMySavedPharmacies
          ? Ga4Event.SAVED_PHARMACIES_VIEWED
          : Ga4Event.CONFIRM_PHARMACIES_VIEWED,
      },
    });
  }, []);

  // Update has MO pharmacy value
  useEffect(() => {
    setHasMailOrderPharmacy(getHasMailOrderPharmacy(state));
  }, [pharmacyType]);

  useEffect(() => {
    if (location.state) {
      const locationState = location.state as LocationWithNextRouteInState;
      if ("nextRoute" in locationState) {
        setNextRoute(locationState.nextRoute);
      }
    }
  }, [location.state]);

  // Event handlers
  const handleRemovePharmacy: PharmacyListItemProps["handleRemoveClick"] = ({
    npi,
    name,
  }) => {
    const removingRetailPharmacy = Boolean(npi);
    removingRetailPharmacy
      ? dispatch({
          type: ActionType.DELETE_PHARMACY,
          payload: npi,
        })
      : // When removing MO, pharmacy type automatically updates to retail
        dispatch({
          type: ActionType.UPDATE_PHARMACY_TYPE,
          payload: PharmacyType.RETAIL,
        });
    hasLocalSession() && dispatch({ type: ActionType.SAVE_PHARMACIES });
    popSuccessfulToast();
    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.PLAN_FINDER_PHARMACY_REMOVED,
        pharmacy_count: removingRetailPharmacy
          ? pharmacies.filter(pharmacy => pharmacy.npi !== npi).length +
            (hasMailOrderPharmacy ? 1 : 0)
          : pharmacies.length, // when removing MO pharmacy, just return pharmacies.length,
        mct_pharmacy_name: removingRetailPharmacy
          ? name || messages["na"]
          : messages["plan_search_prefs.mail_order_pharmacy"],
        mct_pharmacy_type: removingRetailPharmacy
          ? PharmacyType.RETAIL
          : PharmacyType.MAIL,
        text: removePharmacyEventText,
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: tealiumEventAction,
        event_label: removePharmacyEventLabel,
      },
    });
  };

  const handleManagePharmacies = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    const ga4EventText: string =
      messages[managePharmaciesButtonTranslationKey] || "";
    const tealiumEventLabel = isMySavedPharmacies
      ? "Pharmacies managed"
      : ga4EventText;
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: tealiumEventAction,
        event_label: tealiumEventLabel,
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: isMySavedPharmacies
          ? Ga4Event.PLAN_FINDER_SAVED_PHARMACIES_MANAGED
          : Ga4Event.PHARMACY_SELECTED,
        text: ga4EventText,
      },
    });
    isMySavedPharmacies &&
      clearMbpReferrerUriAndRedirect({ dispatch, mbpReferrerUri, e });
  };

  const handleAddPharmacy = () => {
    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.PLAN_FINDER_PHARMACY_SEARCH_RESULTS,
        text: addPharmacyEventText,
        pharmacy_count: pharmacies.length + (hasMailOrderPharmacy ? 1 : 0),
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: tealiumEventAction,
        event_label: addPharmacyEventText,
      },
    });
  };

  // Don't pass npi or name to handleRemovePharmacy for MO
  const handleRemoveMailOrderPharmacy = () => handleRemovePharmacy({});

  const popSuccessfulToast = (): void => {
    if (!toast.isActive(toastId)) {
      toast(
        <div
          className="ManagePharmacies__toast ds-u-fill--primary-alt-lightest
          ds-u-padding--2"
        >
          <strong>{t("manage_pharmacies.toast.success")}</strong>
          <p className="ds-u-margin--0">
            {t("manage_pharmacies.toast.successfully_removed_pharmacy")}
          </p>
        </div>,
        {
          toastId,
          containerId: "globalToastContainer",
          autoClose: 5000,
          className: "ds-u-padding--0",
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          hideProgressBar: true,
          closeButton: false,
        }
      );
    }
  };

  const managePharmaciesDefaultButtonClassNames =
    "ds-c-button ds-u-margin-top--2 ds-u-md-margin-top--0 mct-u-width--full mct-u-md-width--auto";
  const addPharmaciesE2eClassName = "e2e-add-pharmacies";
  const managePharmaciesButtonClassNames = {
    primary: classNames(
      managePharmaciesDefaultButtonClassNames,
      "ds-c-button--solid",
      "ds-u-md-margin-right--4",
      {
        "e2e-see-plans": !isMySavedPharmacies,
      },
      {
        "e2e-referrer-uri": isMySavedPharmacies,
      }
    ),
    secondary: managePharmaciesDefaultButtonClassNames,
  };

  const SavedPharmaciesFooterPrimaryActionButton = () => {
    if (nextRoute) {
      return (
        <Link
          to={nextRoute as string}
          className={managePharmaciesButtonClassNames.primary}
        >
          {t(managePharmaciesButtonTranslationKey)}
        </Link>
      );
    }
    if (isMySavedPharmacies) {
      if (isFromSummaryPage) {
        return (
          <Link
            to={routes.summary.landingPage}
            className={managePharmaciesButtonClassNames.primary}
          >
            {t(managePharmaciesButtonTranslationKey)}
          </Link>
        );
      } else {
        return (
          <a
            className={managePharmaciesButtonClassNames.primary}
            href={mbpReferrerUri}
            onClick={handleManagePharmacies}
          >
            {t(managePharmaciesButtonTranslationKey)}
          </a>
        );
      }
    } else {
      return (
        <Link
          to={`${routes.searchResults}${search}`}
          className={managePharmaciesButtonClassNames.primary}
          onClick={handleManagePharmacies}
        >
          {t(managePharmaciesButtonTranslationKey)}
        </Link>
      );
    }
  };

  const SavedPharmaciesFooterSecondaryActionButton = () => {
    if (isMySavedPharmacies) {
      return (
        <BeneNewPlanSearchLink
          className={classNames(
            managePharmaciesButtonClassNames.secondary,
            "e2e-start-new-plan-search",
            "ds-c-button",
            "ds-c-button--solid",
            "ds-c-button--alternate"
          )}
          messageId="manage_pharmacies.start_new_plan_search"
          beforeRoutingCallback={() => {
            clearMbpReferrerUri(dispatch);
            dispatch({
              type: AnalyticsActionType.SEND_GA4_EVENT,
              settings: {
                event_name: Ga4Event.PLAN_FINDER_STARTED,
                text: t("manage_pharmacies.start_new_plan_search"),
              },
            });
            dispatch({
              type: AnalyticsActionType.SEND_TEALIUM_EVENT,
              settings: {
                event_action: "Find Plans Landing Page - Login",
                event_label: t("manage_pharmacies.start_new_plan_search"),
              },
            });
          }}
        />
      );
    } else {
      return (
        <Link
          to={`${routes.pharmacy}${search}`}
          className={classNames(
            managePharmaciesButtonClassNames.secondary,
            addPharmaciesE2eClassName
          )}
          onClick={handleAddPharmacy}
        >
          {t("manage_pharmacies.actions.add_another_pharmacy")}
        </Link>
      );
    }
  };

  return (
    <AppPage
      heading={
        isMySavedPharmacies
          ? t("manage_pharmacies.my_saved_pharmacies")
          : t("manage_pharmacies.heading")
      }
      outerClassName="ManagePharmacies"
    >
      <div className="ManagePharmacies__list-container">
        <ul className="ds-c-list--bare">
          {hasMailOrderPharmacy && (
            <PharmacyListItem
              pharmacy={{
                npi: "mail-order",
                name: t("pharm_selection.mail_order_pharm"),
                preferred: false,
                in_network: false,
                city: "",
                phone: "",
                state: "",
                street: "",
                unit: "",
                zipcode: "",
              }}
              handleRemoveClick={handleRemoveMailOrderPharmacy}
              shouldHaveBorder={pharmacies.length > 0}
            />
          )}
          {pharmacies.map((pharmacy, index) => (
            <PharmacyListItem
              pharmacy={pharmacy}
              key={pharmacy.npi}
              shouldHaveBorder={index !== pharmacies.length - 1}
              handleRemoveClick={handleRemovePharmacy}
            />
          ))}
        </ul>
        {isMySavedPharmacies && (
          <>
            {noPharmaciesAdded && (
              <p className="ds-u-margin-top--0">
                {t("manage_pharmacies.no_pharmacies_added")}
              </p>
            )}
            <Link
              to={{
                pathname: routes.pharmacy,
                search,
                state: { nextRoute: location.pathname },
              }}
              className={classNames(
                managePharmaciesButtonClassNames.secondary,
                "ds-u-margin-bottom--6",
                addPharmaciesE2eClassName
              )}
              onClick={handleAddPharmacy}
            >
              {noPharmaciesAdded
                ? t("manage_pharmacies.actions.add_a_pharmacy")
                : t("manage_pharmacies.actions.add_another_pharmacy")}
            </Link>
          </>
        )}
      </div>
      <hr className="ds-u-border--1" />
      <div className="ManagePharmacies__footer mct-hide-print ds-u-margin-top--3 ds-u-margin-bottom--6 ds-u-display--flex ds-u-flex-wrap--wrap">
        {SavedPharmaciesFooterPrimaryActionButton()}
        {!isFromSummaryPage && SavedPharmaciesFooterSecondaryActionButton()}
      </div>
    </AppPage>
  );
};

export default ManagePharmaciesPage;
